/**
 * Raw-data CSV columns (spec §4 column map). Rows come from getCSVData with isHeader 'yes',
 * so each row is an object keyed by header name.
 */
export const RAW_COL_TEMPERATURE_TIME = 'Temperature Timestamps';
export const RAW_COL_ADC_TIME = 'ADC Timestamps';
export const RAW_COL_PID_TIME = 'PID Timestamp';
export const RAW_COL_PID_SIGNAL = 'PID Signal';
export const RAW_COL_PID_TEMPERATURE = 'PID Temperature';

export interface PidSeries {
   time: number[];
   signal: number[];
   temperature: number[];
}

const toNumber = (value: any): number | null => {
   const n = parseFloat(value);
   return Number.isFinite(n) ? n : null;
};

/**
 * Time of the temperature channels (KP, Column, PCF, Injector, Thermistor). Files written before
 * the column rename only carry ADC Timestamps, so fall back to it to keep history rendering (AS-3).
 */
export const temperatureTimestamp = (row: object): number =>
   parseFloat(row.hasOwnProperty(RAW_COL_TEMPERATURE_TIME) ? row[RAW_COL_TEMPERATURE_TIME] : row[RAW_COL_ADC_TIME]);

/**
 * PID samples of a raw-data file. Rows without a PID Timestamp are skipped: old files have no PID
 * columns at all, and the PID may log fewer samples than the other channels.
 */
export const readPidSeries = (rows: object[]): PidSeries => {
   const series: PidSeries = { time: [], signal: [], temperature: [] };
   for (const row of rows || []) {
      const time = toNumber(row[RAW_COL_PID_TIME]);
      if (time === null) {
         continue;
      }
      series.time.push(time);
      series.signal.push(toNumber(row[RAW_COL_PID_SIGNAL]));
      series.temperature.push(toNumber(row[RAW_COL_PID_TEMPERATURE]));
   }
   return series;
};

export const hasPidSeries = (pid: PidSeries): boolean => !!pid && pid.time.length > 0;

// One colour for the PID wherever it is drawn, clear of the default palette the other traces use.
const PID_COLOR = '#9467bd';

/** PID Temperature on the temperature chart's °C axis, next to the Injector trace. */
export const pidTemperatureTrace = (pid: PidSeries) => ({
   x: pid.time,
   y: pid.temperature,
   type: 'scatter',
   mode: 'lines',
   name: 'PID Temperature',
   line: { color: PID_COLOR },
});

/**
 * PID Signal overlaid on the capacitance chart, so PID and capacitance peaks line up on the shared
 * time axis (D-11). It is in mV, so it sits on its own right-hand axis (PID_SIGNAL_YAXIS).
 */
export const pidSignalTrace = (pid: PidSeries) => ({
   x: pid.time,
   y: pid.signal,
   type: 'scatter',
   mode: 'lines',
   name: 'PID Signal',
   yaxis: 'y2',
   line: { color: PID_COLOR },
});

export const PID_SIGNAL_YAXIS = {
   title: { text: 'Voltage (mV)' },
   overlaying: 'y',
   side: 'right',
   showgrid: false,
   automargin: true,
};

/**
 * PID Signal of one cycle on a multi-cycle capacitance overlay: drawn in the cycle's colour, dotted
 * so it reads apart from that cycle's capacitance line.
 */
export const pidSignalCycleTrace = (pid: PidSeries, name: string, color: string) => ({
   ...pidSignalTrace(pid),
   name,
   line: { color, dash: 'dot' },
});

/** Adds the PID Signal axis to a capacitance layout when at least one PID trace is plotted on it. */
export const withPidSignalAxis = (layout: object, hasPidTrace: boolean): object =>
   hasPidTrace ? { ...layout, yaxis2: PID_SIGNAL_YAXIS } : layout;
